import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getJobs, getOperations, compareOperation, getExcelDownloadUrl, deleteOperation, exportExcelJob } from './services/excelApi';

export default function ExcelComparisonView() {
  const [jobs, setJobs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [baseJobs, setBaseJobs] = useState([]);
  const [referenceJobs, setReferenceJobs] = useState([]);
  const [columns, setColumns] = useState(['Mobile']);
  
  const [normalization, setNormalization] = useState({
    removeSpaces: true,
    removeDashes: true,
    removePlus: true,
    removeLeadingZero: false,
    removeCountryCode: true,
    keepDigitsOnly: true
  });

  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadJobs();
    loadOperations();
    const interval = setInterval(() => {
      loadJobs();
      loadOperations();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  async function loadOperations() {
    try {
      const data = await getOperations();
      setOperations(data || []);
    } catch (e) {}
  }

  async function loadJobs() {
    try {
      const data = await getJobs();
      setJobs(data || []);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }

  async function handleCompare() {
    if (baseJobs.length === 0) {
      toast.error('Select at least one Base File');
      return;
    }
    if (referenceJobs.length === 0) {
      toast.error('Select at least one Reference File');
      return;
    }
    const cols = columns.filter(c => c.trim() !== '');
    if (cols.length === 0) {
      toast.error('Enter at least one column to compare by');
      return;
    }

    setComparing(true);
    try {
      await compareOperation(baseJobs, referenceJobs, cols, normalization);
      toast.success('Multi-file comparison started!');
      setBaseJobs([]);
      setReferenceJobs([]);
      loadOperations();
    } catch (e) {
      toast.error(e.message || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  }

  function toggleJobSelection(id, list, setList) {
    if (list.includes(id)) {
      setList(list.filter(j => j !== id));
    } else {
      setList([...list, id]);
    }
  }

  async function handleDeleteOp(id) {
    if (!window.confirm('Delete this operation?')) return;
    try {
      await deleteOperation(id);
      toast.success('Operation deleted');
      loadOperations();
    } catch (e) {
      toast.error('Failed to delete operation');
    }
  }

  async function handleExport(jobId) {
    try {
      await exportExcelJob(jobId);
      toast.success('Export started!');
      loadJobs();
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  }

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="panel__header">
        <div className="panel__header--split">
          <div>
            <span className="panel__eyebrow">Data Hub</span>
            <h2>Excel Comparison</h2>
            <p>Compare two massive datasets and remove matching records efficiently.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', minWidth: '300px' }}>
          <h3 style={{ marginBottom: '16px' }}>Excel 1 (Base Files)</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '16px' }}>Select the files from which you want to REMOVE matches.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
            {jobs.map(j => (
              <label key={j.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="checkbox" checked={baseJobs.includes(j.id)} onChange={() => toggleJobSelection(j.id, baseJobs, setBaseJobs)} />
                <span style={{ fontSize: '0.9rem' }}>{j.originalName} ({j.totalRows} rows)</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', minWidth: '300px' }}>
          <h3 style={{ marginBottom: '16px' }}>Excel 2 (Reference Files)</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '16px' }}>Select the files containing the records to match against.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
            {jobs.map(j => (
              <label key={j.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="checkbox" checked={referenceJobs.includes(j.id)} onChange={() => toggleJobSelection(j.id, referenceJobs, setReferenceJobs)} />
                <span style={{ fontSize: '0.9rem' }}>{j.originalName} ({j.totalRows} rows)</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ marginBottom: '16px' }}>Comparison Logic</h3>
        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '16px' }}>
          Enter the exact column names (comma separated logic unsupported here yet, add multiple fields below) to match against. Records in Excel 1 that match Excel 2 on these columns will be removed from Excel 1.
        </p>

        {columns.map((col, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input 
              type="text" 
              className="form-input" 
              placeholder="e.g. Mobile"
              value={col}
              onChange={e => {
                const newCols = [...columns];
                newCols[index] = e.target.value;
                setColumns(newCols);
              }}
              style={{ maxWidth: '300px' }}
            />
            {index === columns.length - 1 && (
              <button className="button button--secondary" onClick={() => setColumns([...columns, ''])}>+ Add Column</button>
            )}
          </div>
        ))}

        <div style={{ marginTop: '24px' }}>
          <h4 style={{ marginBottom: '12px' }}>Normalization Settings</h4>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={normalization.removeSpaces} onChange={e => setNormalization({...normalization, removeSpaces: e.target.checked})} />
              Remove Spaces
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={normalization.removeDashes} onChange={e => setNormalization({...normalization, removeDashes: e.target.checked})} />
              Remove Dashes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={normalization.removePlus} onChange={e => setNormalization({...normalization, removePlus: e.target.checked})} />
              Remove Plus (+)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={normalization.removeCountryCode} onChange={e => setNormalization({...normalization, removeCountryCode: e.target.checked})} />
              Remove Country Code (91)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={normalization.keepDigitsOnly} onChange={e => setNormalization({...normalization, keepDigitsOnly: e.target.checked})} />
              Keep Digits Only
            </label>
          </div>
        </div>

        <div style={{ marginTop: '24px' }}>
          <button className="button button--primary" onClick={handleCompare} disabled={comparing}>
            {comparing ? 'Starting...' : 'Compare & Remove Matches'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Recent Operations</h3>
        {operations.filter(op => op.type === 'compare').map(op => (
          <div key={op.id} style={{ background: '#f0fdf4', padding: '24px', borderRadius: '12px', border: '1px solid #bbf7d0', marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ color: '#166534' }}>Comparison Operation: {op.id}</h3>
              {op.status === 'processing' || op.status === 'pending' ? (
                <span style={{ padding: '4px 12px', background: '#dcfce3', color: '#166534', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>Comparing... {op.progress}%</span>
              ) : op.status === 'error' ? (
                <span style={{ padding: '4px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>Error</span>
              ) : (
                <span style={{ padding: '4px 12px', background: '#dcfce3', color: '#166534', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>Completed</span>
              )}
            </div>

            {op.stats && (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', flex: 1, minWidth: '150px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase' }}>Removed Records</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{op.stats.totalRemoved?.toLocaleString() || 0}</div>
                </div>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', flex: 1, minWidth: '150px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase' }}>Final Records</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{op.stats.totalAfter?.toLocaleString() || 0}</div>
                </div>
              </div>
            )}
            
            {op.resultJobs && op.resultJobs.length > 0 && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h4 style={{ fontSize: '0.9rem', color: '#64748b', margin: 0 }}>Generated Files:</h4>
                {op.resultJobs.map(jobId => {
                  const rJob = jobs.find(j => j.id === jobId);
                  if (!rJob) return <div key={jobId} style={{ fontSize: '0.85rem' }}>Processing {jobId}...</div>;
                  return (
                    <div key={jobId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{rJob.originalName}</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {rJob.status === 'exporting' && <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Exporting... {rJob.progress}%</span>}
                        {rJob.status === 'ready' && !rJob.filename && (
                          <button className="button button--secondary" onClick={() => handleExport(rJob.id)} style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                            Generate Excel
                          </button>
                        )}
                        {rJob.filename && (
                          <a href={getExcelDownloadUrl(rJob.filename)} target="_blank" rel="noreferrer" download className="button button--primary" style={{ padding: '6px 12px', fontSize: '0.85rem', background: '#10b981', borderColor: '#10b981' }}>
                            Download
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="button button--secondary" style={{ color: '#ef4444', borderColor: '#ef4444', padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => handleDeleteOp(op.id)}>Delete Operation</button>
            </div>
          </div>
        ))}
      </div>


    </div>
  );
}
